import {
  getRuntimeToolManifest,
  type RuntimeToolArtifactType,
  type RuntimeToolId,
  type RuntimeToolManifestEntry,
} from '@/lib/runtimeTools/manifest';

export type ChatQuickActionKind =
  | 'review'
  | 'fact-check'
  | 'repurpose'
  | 'pitch'
  | 'crisis'
  | 'insights';

export type ChatQuickAction = {
  kind: ChatQuickActionKind;
  label: string;
  toolId: RuntimeToolId;
  manifest: RuntimeToolManifestEntry;
  disabled: boolean;
  missingReason?: string;
};

type ChatQuickActionDefinition = {
  kind: ChatQuickActionKind;
  label: string;
  toolId: RuntimeToolId;
};

export type ChatQuickActionContext = {
  hasArtifact: boolean;
  artifactType?: RuntimeToolArtifactType | null;
  hasConversation: boolean;
};

const CHAT_QUICK_ACTIONS: ChatQuickActionDefinition[] = [
  { kind: 'review', label: 'Review', toolId: 'press_release_review' },
  { kind: 'fact-check', label: 'Fact-check', toolId: 'fact_checker' },
  { kind: 'repurpose', label: 'Repurpose', toolId: 'repurpose' },
  { kind: 'pitch', label: 'Pitch', toolId: 'media_pitch' },
  { kind: 'crisis', label: 'Crisis', toolId: 'crisis' },
  { kind: 'insights', label: 'Insights', toolId: 'insights_exec' },
];

function formatArtifactTypes(types: RuntimeToolArtifactType[] | undefined): string {
  if (!types?.length) return 'supported artifact';
  return types.map((type) => type.replace(/_/g, ' ')).join(', ');
}

function getMissingReason(
  manifest: RuntimeToolManifestEntry,
  context: ChatQuickActionContext,
): string | undefined {
  if (!manifest.surfaces.includes('chat')) {
    return 'Unavailable in chat';
  }
  if (manifest.requiresArtifact && !context.hasArtifact) {
    return 'Attach an artifact first';
  }
  if (manifest.requiresConversation && !context.hasConversation) {
    return 'Open a conversation first';
  }
  if (
    context.hasArtifact
    && context.artifactType
    && manifest.supportedArtifactTypes?.length
    && !manifest.supportedArtifactTypes.includes(context.artifactType)
  ) {
    return `Requires ${formatArtifactTypes(manifest.supportedArtifactTypes)}`;
  }
  return undefined;
}

export function listChatQuickActions(context: ChatQuickActionContext): ChatQuickAction[] {
  return CHAT_QUICK_ACTIONS
    .map((definition) => {
      const manifest = getRuntimeToolManifest(definition.toolId);
      if (!manifest) return null;
      const missingReason = getMissingReason(manifest, context);
      return {
        ...definition,
        manifest,
        disabled: !!missingReason,
        ...(missingReason ? { missingReason } : {}),
      };
    })
    .filter((action): action is ChatQuickAction => !!action);
}

